import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * Chip radius comes from `--radius-chip` (9px), which `.dark-teal` declares. It is
 * not registered in `tailwind.config.js` and the other four themes do not declare
 * it, so the arbitrary value carries a tokenized fallback to the `rounded-md`
 * expression (`calc(var(--radius) - 2px)` = 10px). Once `borderRadius.chip` is
 * registered this collapses to plain `rounded-chip`.
 */
const CHIP_RADIUS = "rounded-[var(--radius-chip,calc(var(--radius)_-_2px))]";

/**
 * Every tinted variant is `bg-<token>/10` + `text-<token>` + a `border-<token>/30`
 * hairline. On a near-black canvas the tint sits under the text instead of
 * competing with it, so the text keeps the token's own luminance and clears 4.5:1.
 *
 * Provider brand colours are frozen — the values in `tailwind.config.js` are the
 * brands' own and are meant to stand out. Where a brand's base tier measures below
 * 4.5:1 on the dark canvas (azure 3.95, openrouter 4.22, gcp 4.92) the variant
 * switches to that same brand's already-registered `-light` tier under `dark:`.
 * That is a tier selection, not a recolour.
 */
const badgeVariants = cva(
  cn(
    "inline-flex items-center border px-2.5 py-0.5 text-xs font-medium",
    CHIP_RADIUS,
    "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
  ),
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground",
        secondary: "border-transparent bg-secondary text-secondary-foreground",
        destructive: "border-destructive/30 bg-destructive/10 text-destructive",
        outline: "border-border text-foreground",
        success: "border-success/30 bg-success/10 text-success",
        warning: "border-warning/30 bg-warning/10 text-warning",
        info: "border-info/30 bg-info/10 text-info",
        // Cloud providers
        aws: "border-aws/30 bg-aws/10 text-aws",
        azure: "border-azure/30 bg-azure/10 text-azure dark:text-azure-light",
        gcp: "border-gcp/30 bg-gcp/10 text-gcp dark:text-gcp-light",
        // LLM / API providers. `google_ai` and `azure_openai` reuse the Google and
        // Microsoft brand tokens rather than a raw palette blue — the same mapping
        // AddCredentialDialog.tsx:218-219 already applies to their icons.
        openai: "border-openai/30 bg-openai/10 text-openai",
        anthropic: "border-anthropic/30 bg-anthropic/10 text-anthropic",
        openrouter:
          "border-openrouter/30 bg-openrouter/10 text-openrouter dark:text-openrouter-light",
        google_ai: "border-gcp/30 bg-gcp/10 text-gcp dark:text-gcp-light",
        azure_openai:
          "border-azure/30 bg-azure/10 text-azure dark:text-azure-light",
        aws_bedrock: "border-aws/30 bg-aws/10 text-aws",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

export interface BadgeProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

/**
 * Forwards its ref so a badge can be the trigger of a `Hint`, which merges the
 * trigger onto its child and needs the element to position against.
 */
const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(function Badge(
  { className, variant, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
});

// shadcn cva pattern: variants are intentional non-component exports
// eslint-disable-next-line react-refresh/only-export-components
export { Badge, badgeVariants };
